import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Table,
  Button,
  Input,
  Select,
  Modal,
  message,
  Tag,
  Space,
  Card,
  Row,
  Col,
  DatePicker,
  Tooltip,
  Popconfirm,
  Typography,
  Image,
  Form,
  Radio,
  InputNumber,
  Alert
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  UserOutlined,
  CalendarOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { interventionsService, Intervention } from '../services/interventionsService';
import { TechnicianSuggestionsModal } from '../components/TechnicianSuggestionsModal';
import { customersService, Customer } from '../services/customersService';
import { techniciansService } from '../services/techniciansService';
import { getImageUrl } from '../services/uploadService';
import dayjs from 'dayjs';
import UserAvatar from '../components/Common/UserAvatar';
import type { ColumnsType } from 'antd/es/table';
import { exportToCSV } from '../utils/exportUtils';
import { useAuth } from '../contexts/AuthContext';

const { Title } = Typography;

interface Technician {
  id: number;
  user_id?: number;
  first_name: string;
  last_name: string;
  availability_status?: string;
}

const OPERATIONAL_CITIES = [
  'Abidjan',
  'Assinie',
  'Grand-Bassam',
  'Bingerville',
  'Songon',
  'Anyama'
];

const ABIDJAN_COMMUNES = [
  'Abobo',
  'Adjamé',
  'Attécoubé',
  'Cocody',
  'Koumassi',
  'Marcory',
  'Plateau',
  'Port-Bouët',
  'Treichville',
  'Yopougon'
];

const InterventionsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const location = useLocation();
  const navigate = useNavigate();
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [rescheduleModal, setRescheduleModal] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState<any>(null);
  const [viewModal, setViewModal] = useState(false);
  const [suggestionsModal, setSuggestionsModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedIntervention, setSelectedIntervention] = useState<Intervention | null>(null);

  // État du modal de remboursement
  const [refundModal, setRefundModal] = useState(false);
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [refundCanal, setRefundCanal] = useState('wave');
  const [refundPhone, setRefundPhone] = useState('');
  const [refundAmount, setRefundAmount] = useState<number | undefined>(undefined);
  const [refundReason, setRefundReason] = useState('');
  const [checkingRefund, setCheckingRefund] = useState(false);
  
  // Filtres
  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
  const [filterPriority, setFilterPriority] = useState<string | undefined>(undefined);
  const [filterType, setFilterType] = useState<string | undefined>(undefined);
  const [filterTechnician, setFilterTechnician] = useState<number | undefined>(undefined);
  const [filterConfirmation, setFilterConfirmation] = useState<string | undefined>(undefined);
  const [searchText, setSearchText] = useState('');

  const [newIntervention, setNewIntervention] = useState({
    title: '',
    description: '',
    intervention_type: 'maintenance' as string,
    climatiseur_type: undefined as string | undefined,
    customer_id: undefined as number | undefined,
    technician_id: undefined as number | undefined,
    city: 'Abidjan' as string | undefined,
    commune: undefined as string | undefined,
    address: '' as string,
    scheduled_date: null as any,
    priority: 'medium' as 'low' | 'medium' | 'high' | 'critical',
    status: 'pending' as string,
    equipment_count: 1
  });

  const interventionTypeMap = {
    maintenance: { label: 'Entretien / Maintenance', color: 'blue', icon: '🔧' },
    repair: { label: 'Dépannage / Réparation', color: 'orange', icon: '🛠️' },
    installation: { label: 'Installation', color: 'cyan', icon: '❄️' },
    diagnostic: { label: 'Diagnostic', color: 'purple', icon: '🔍' }
  };

  const statusMap = {
    pending: { label: 'En attente', color: 'orange', icon: '⏳' },
    assigned: { label: 'Assignée', color: 'purple', icon: '👤' },
    accepted: { label: 'Acceptée', color: 'cyan', icon: '✓' },
    on_the_way: { label: 'En route', color: 'blue', icon: '🚗' },
    arrived: { label: 'Arrivé', color: 'geekblue', icon: '📍' },
    in_progress: { label: 'En cours', color: 'processing', icon: '⚙️' },
    completed: { label: 'Terminée', color: 'green', icon: '✅' },
    cancelled: { label: 'Annulée', color: 'red', icon: '❌' },
    client_unreachable: { label: 'Injoignable', color: 'volcano', icon: '⚠️' },
    diagnostic_submitted: { label: 'Diagnostic soumis', color: 'cyan', icon: '📝' },
    execution_confirmed: { label: 'Travaux confirmés', color: 'green', icon: '🎉' },
    scheduled: { label: 'Programmée', color: 'blue', icon: '📅' }
  };

  const priorityMap = {
    low: { label: 'Faible', color: 'green', icon: '🔵' },
    normal: { label: 'Normale', color: 'blue', icon: '🟢' },
    medium: { label: 'Moyenne', color: 'orange', icon: '🟠' },
    high: { label: 'Élevée', color: 'red', icon: '🔴' },
    urgent: { label: 'Urgente', color: 'magenta', icon: '🚨' },
    critical: { label: 'Critique', color: 'volcano', icon: '⚠️' }
  };

  const { id: routeParamId } = useParams<{ id?: string }>();

  useEffect(() => {
    loadInterventions();
    loadCustomers();
    loadTechnicians();
  }, []);

  // Gérer le paramètre id dans l'URL (/interventions/:id ou /interventions?id=:id) pour ouvrir automatiquement la modal
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const interventionId = routeParamId || searchParams.get('id');
    
    if (interventionId) {
      const id = parseInt(interventionId, 10);
      if (isNaN(id)) return;

      const intervention = interventions.find(i => i.id === id);
      
      if (intervention) {
        handleViewIntervention(intervention);
        window.history.replaceState({}, '', '/interventions');
      } else {
        // Essayer de charger directement depuis l'API
        interventionsService.getInterventionById(id)
          .then(fullIntervention => {
            setSelectedIntervention(fullIntervention);
            setViewModal(true);
            window.history.replaceState({}, '', '/interventions');
          })
          .catch(err => {
            console.error('Intervention non trouvée:', err);
            message.error('Intervention non trouvée');
            window.history.replaceState({}, '', '/interventions');
          });
      }
    }
  }, [location.search, routeParamId, interventions]);

  const loadInterventions = async () => {
    try {
      setLoading(true);
      const response = await interventionsService.getInterventions({ limit: 1000 });
      setInterventions(response.data.interventions);
    } catch (error) {
      message.error('Erreur lors du chargement des interventions');
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await customersService.getCustomers({ limit: 1000 });
      setCustomers(response.data.customers);
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
    }
  };

  const loadTechnicians = async () => {
    try {
      const response = await techniciansService.getTechnicians({ limit: 1000 });
      setTechnicians(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des techniciens:', error);
    }
  };

  const handleAddIntervention = () => {
    setEditMode(false);
    setNewIntervention({
      title: '',
      description: '',
      intervention_type: 'maintenance',
      climatiseur_type: undefined,
      customer_id: undefined,
      technician_id: undefined,
      city: 'Abidjan',
      commune: undefined,
      address: '',
      scheduled_date: null,
      priority: 'medium',
      status: 'pending',
      equipment_count: 1
    });
    setOpenModal(true);
  };

  const handleEditIntervention = (intervention: Intervention) => {
    // Vérifier si le customer_id existe dans la liste
    setEditMode(true);
    setSelectedIntervention(intervention);

    const cust: any = intervention.customer || (intervention.customer_id ? combinedCustomers.find(c => Number(c.id) === Number(intervention.customer_id) || (c.user_id && Number(c.user_id) === Number(intervention.customer_id))) : null);
    const initialCity = intervention.city || cust?.city || (cust?.commune && !['Grand-Bassam', 'Assinie', 'Songon', 'Anyama', 'Bingerville'].includes(cust.commune) ? 'Abidjan' : cust?.commune) || 'Abidjan';
    const initialCommune = intervention.commune || cust?.commune || undefined;
    const initialAddress = intervention.address || (intervention as any).location_address || cust?.address || '';

    setNewIntervention({
      title: intervention.title,
      description: intervention.description || '',
      intervention_type: intervention.intervention_type || 'maintenance',
      climatiseur_type: intervention.climatiseur_type || undefined,
      customer_id: intervention.customer_id ? Number(intervention.customer_id) : undefined,
      technician_id: intervention.technician_id ? Number(intervention.technician_id) : undefined,
      city: initialCity,
      commune: initialCommune,
      address: initialAddress,
      scheduled_date: intervention.scheduled_date ? dayjs(intervention.scheduled_date) : null,
      priority: intervention.priority,
      status: intervention.status,
      equipment_count: intervention.equipment_count || 1
    });
    setOpenModal(true);
  };

  const handleViewIntervention = async (intervention: Intervention) => {
    try {
      const fullIntervention = await interventionsService.getInterventionById(intervention.id);
      setSelectedIntervention(fullIntervention);
      setViewModal(true);
    } catch (error) {
      console.error('Erreur chargement intervention:', error);
      setSelectedIntervention(intervention);
      setViewModal(true);
    }
  };

  const handleDeleteIntervention = async (id: number) => {
    try {
      await interventionsService.deleteIntervention(id);
      message.success('Intervention supprimée avec succès');
      loadInterventions();
    } catch (error) {
      message.error('Erreur lors de la suppression');
      console.error('Erreur:', error);
    }
  };

  const handleReschedule = async () => {
    if (!selectedIntervention || !rescheduleDate) {
      message.error('Veuillez sélectionner une nouvelle date');
      return;
    }
    try {
      const response = await interventionsService.reschedule(selectedIntervention.id, rescheduleDate.toDate().toISOString());
      if (response) {
        message.success('Intervention reprogrammée avec succès');
        setRescheduleModal(false);
        setViewModal(false);
        loadInterventions();
      }
    } catch (error: any) {
      console.error('Erreur lors de la reprogrammation:', error);
      message.error(error.response?.data?.message || 'Erreur lors de la reprogrammation');
    }
  };

  const handleExecuteRefund = async () => {
    if (!selectedIntervention) return;
    if (!isSuperAdmin) {
      message.error('Seul le super administrateur est habilité à effectuer un remboursement Mobile Money.');
      return;
    }
    const phone = (refundPhone || '').trim();
    if (!phone || phone.length < 8) {
      message.error('Veuillez renseigner un numéro de téléphone Mobile Money valide');
      return;
    }
    const amt = Number(refundAmount);
    if (!amt || amt <= 0) {
      message.error('Le montant à rembourser doit être supérieur à 0');
      return;
    }

    Modal.confirm({
      title: 'Confirmer le virement Mobile Money ?',
      content: `Vous allez transférer ${amt.toLocaleString()} FCFA via ${refundCanal.toUpperCase()} au numéro ${phone}. Les fonds seront immédiatement débités de votre compte FineoPay.`,
      okText: 'Confirmer et transférer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        setRefundSubmitting(true);
        try {
          const res = await interventionsService.refundPayout(selectedIntervention.id, {
            canal: refundCanal,
            clientAccount: phone,
            amount: amt,
            reason: refundReason
          });
          message.success(res.message || 'Remboursement envoyé avec succès !');
          setRefundModal(false);
          const updated = await interventionsService.getInterventionById(selectedIntervention.id);
          setSelectedIntervention(updated);
          loadInterventions();
        } catch (err: any) {
          console.error('Erreur remboursement FineoPay:', err);
          message.error(err.response?.data?.message || err.message || 'Erreur lors du virement');
        } finally {
          setRefundSubmitting(false);
        }
      }
    });
  };

  const handleCheckRefundStatus = async (interventionId: number) => {
    try {
      setCheckingRefund(true);
      const res = await interventionsService.checkRefundStatus(interventionId);
      if (res.success) {
        message.info(res.message);
        const updated = await interventionsService.getInterventionById(interventionId);
        setSelectedIntervention(updated);
        loadInterventions();
      } else {
        message.warning(res.message);
      }
    } catch (err: any) {
      console.error('Erreur vérification remboursement:', err);
      message.error(err.response?.data?.message || err.message || 'Erreur lors de la vérification');
    } finally {
      setCheckingRefund(false);
    }
  };

  const handleStatusChange = async (interventionId: number, newStatus: string) => {
    try {
      await interventionsService.updateStatus(interventionId, newStatus);
      message.success('Statut mis à jour avec succès');
      loadInterventions();
    } catch (error) {
      message.error('Erreur lors de la mise à jour du statut');
      console.error('Erreur:', error);
    }
  };

  const handleResolveDispute = async (interventionId: number) => {
    try {
      await interventionsService.updateIntervention(interventionId, {
        customer_confirmed: true,
        customer_rejection_reason: null
      });
      message.success('Contestation marquée comme traitée');
      setViewModal(false);
      loadInterventions();
    } catch (error) {
      message.error('Erreur lors de la résolution de la contestation');
      console.error('Erreur:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      const interventionData = {
        ...newIntervention,
        scheduled_date: newIntervention.scheduled_date ? newIntervention.scheduled_date.toISOString() : null
      };

      if (editMode && selectedIntervention) {
        // Vérifier si le technicien a changé
        const technicianChanged = 
          selectedIntervention.technician_id !== newIntervention.technician_id;
        
        if (technicianChanged && newIntervention.technician_id) {
          // Si le technicien a changé, utiliser la route d'assignation spécifique
          await interventionsService.assignTechnician(
            selectedIntervention.id, 
            newIntervention.technician_id
          );
          message.success('Technicien assigné avec succès');
          
          // Mettre à jour les autres champs si nécessaire
          const otherChanges = { ...interventionData };
          delete otherChanges.technician_id; // Déjà mis à jour
          
          if (Object.keys(otherChanges).some(key => 
            otherChanges[key as keyof typeof otherChanges] !== 
            selectedIntervention[key as keyof Intervention]
          )) {
            await interventionsService.updateIntervention(
              selectedIntervention.id, 
              otherChanges
            );
          }
        } else {
          // Sinon, mise à jour normale
          await interventionsService.updateIntervention(selectedIntervention.id, interventionData);
          message.success('Intervention modifiée avec succès');
        }
      } else {
        await interventionsService.createIntervention(interventionData);
        message.success('Intervention créée avec succès');
      }
      
      setOpenModal(false);
      loadInterventions();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Erreur lors de l\'enregistrement';
      message.error(errorMessage);
      console.error('Erreur:', error);
    }
  };

  const columns: ColumnsType<Intervention> = [
    {
      title: 'Titre',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: Intervention) => {
        const isCancelled = record.status === 'cancelled';
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              {isCancelled && (
                <Tag color="error" style={{ fontWeight: 'bold', margin: 0, fontSize: '11px' }}>
                  ✕ ANNULÉE
                </Tag>
              )}
              <strong style={{
                color: isCancelled ? '#8c8c8c' : 'inherit',
                textDecoration: isCancelled ? 'line-through' : 'none'
              }}>
                {text}
              </strong>
            </div>
            {(record as any).reference && (
              <div style={{ fontSize: '11px', color: '#8c8c8c' }}>{(record as any).reference}</div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Type',
      dataIndex: 'intervention_type',
      key: 'intervention_type',
      render: (type: string, record: Intervention) => {
        const normalized = (type || 'maintenance').toLowerCase();
        const titleLower = (record.title || '').toLowerCase();
        let color = 'blue';
        let label = '🔧 Entretien';
        if (
          ['repair', 'dépannage', 'depannage', 'réparation', 'reparation'].includes(normalized) ||
          titleLower.includes('dépannage') || titleLower.includes('depannage') || titleLower.includes('réparation')
        ) {
          color = 'orange';
          label = '🛠️ Dépannage';
        } else if (normalized === 'installation' || titleLower.includes('installation')) {
          color = 'cyan';
          label = '❄️ Installation';
        } else if (normalized === 'diagnostic' || titleLower.includes('diagnostic')) {
          color = 'purple';
          label = '🔍 Diagnostic';
        }
        return <Tag color={color}>{label}</Tag>;
      }
    },
    {
      title: 'Client',
      dataIndex: 'customer',
      key: 'customer',
      render: (customer: Customer, record: Intervention) => {
        const cust: any = customer || (record.customer_id ? customers.find(c => Number(c.id) === Number(record.customer_id) || Number(c.user_id) === Number(record.customer_id)) : null);
        let name = '';
        if (cust) {
          name = `${cust.first_name || cust.user?.first_name || ''} ${cust.last_name || cust.user?.last_name || ''}`.trim() || cust.company_name || '';
        }
        if (!name && record.customer_id) {
          name = `Client #${record.customer_id}`;
        }
        const locationText = cust?.commune || cust?.city || (record.address ? record.address.split(',').pop()?.trim() : null);

        return (
          <div>
            <div style={{ fontWeight: 500 }}>{name || '-'}</div>
            {locationText && (
              <div style={{ fontSize: '11px', color: '#64748B', display: 'flex', alignItems: 'center', gap: '3px', marginTop: '2px' }}>
                <EnvironmentOutlined style={{ fontSize: '10px', color: '#0A543D' }} />
                <span>{locationText}</span>
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Technicien',
      dataIndex: 'technician',
      key: 'technician',
      render: (technician: any, record: Intervention) => {
        const tech = technician || (record.technician_id ? technicians.find(t => Number(t.id) === Number(record.technician_id)) : null);
        if (tech && (tech.first_name || tech.last_name)) {
          return (
            <Space>
              <UserAvatar
                src={tech.profile_image || tech.profile_picture}
                firstName={tech.first_name}
                lastName={tech.last_name}
                size={28}
              />
              <span>{`${tech.first_name || ''} ${tech.last_name || ''}`.trim()}</span>
            </Space>
          );
        }
        if (record.technician_id) {
          return <Tag color="blue">Technicien #{record.technician_id}</Tag>;
        }
        return <span style={{ color: '#8c8c8c', fontStyle: 'italic' }}>Non assigné</span>;
      }
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: Intervention) => {
        const isCancelled = status === 'cancelled';
        return (
          <Select
            value={status}
            onChange={(newStatus) => handleStatusChange(record.id, newStatus)}
            style={{
              width: 135,
              ...(isCancelled ? {
                backgroundColor: '#fff1f0',
                borderColor: '#ffa39e',
                borderRadius: 6,
                fontWeight: 600
              } : {})
            }}
            className={isCancelled ? 'select-status-cancelled' : ''}
            size="small"
          >
            {Object.entries(statusMap).map(([key, { label, icon }]) => (
              <Select.Option key={key} value={key}>
                <span style={{
                  color: key === 'cancelled' ? '#cf1322' : key === 'completed' ? '#389e0d' : 'inherit',
                  fontWeight: key === 'cancelled' ? 600 : 'normal'
                }}>
                  {icon ? `${icon} ` : ''}{label}
                </span>
              </Select.Option>
            ))}
          </Select>
        );
      }
    },
    {
      title: 'Priorité',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: string) => {
        const priorityInfo = priorityMap[priority as keyof typeof priorityMap];
        return (
          <Tag color={priorityInfo?.color}>
            {priorityInfo?.label || priority}
          </Tag>
        );
      }
    },
    {
      title: 'Date prévue',
      dataIndex: 'scheduled_date',
      key: 'scheduled_date',
      render: (date: string) => date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-'
    },
    {
      title: 'Paiement',
      dataIndex: 'diagnostic_paid',
      key: 'diagnostic_paid',
      render: (_: any, record: Intervention) => {
        const isCancelled = record.status === 'cancelled';
        const isFree = record.is_free_diagnosis || !record.diagnostic_fee || Number(record.diagnostic_fee) === 0;
        const isSplit = record.payment_option === 'split';
        const hasPaid = record.diagnostic_paid || (isSplit && record.second_payment_status === 'paid');

        // Cas d'une intervention annulée
        if (isCancelled) {
          if (record.refund_status === 'refunded') {
            return (
              <Tooltip title={`Remboursé via ${record.refund_canal?.toUpperCase() || 'Mobile Money'} (${record.refund_amount || record.diagnostic_fee} F CFA)`}>
                <Tag color="purple" style={{ fontWeight: 600 }}>
                  ✓ Remboursé
                </Tag>
              </Tooltip>
            );
          }
          if (record.refund_status === 'pending') {
            return (
              <Tooltip title={`Virement en cours de traitement via ${record.refund_canal?.toUpperCase() || 'Mobile Money'} (${record.refund_amount || record.diagnostic_fee} F CFA)`}>
                <Tag color="processing" style={{ fontWeight: 600 }}>
                  ⏳ Virement en cours
                </Tag>
              </Tooltip>
            );
          }
          if (record.refund_status === 'failed') {
            return (
              <Tooltip title={`Échec du virement FineoPay (${record.refund_amount || record.diagnostic_fee} F CFA) — À relancer`}>
                <Tag color="error" style={{ fontWeight: 600, border: '1px solid #ffa39e' }}>
                  ⚠️ Remboursement échoué
                </Tag>
              </Tooltip>
            );
          }
          if (hasPaid) {
            return (
              <Tooltip title="Paiement perçu sur une intervention annulée — Remboursement à effectuer">
                <Tag color="error" style={{ fontWeight: 600, border: '1px solid #ffa39e' }}>
                  ⚠️ Payé (À rembourser)
                </Tag>
              </Tooltip>
            );
          }
          return <Tag color="default" style={{ color: '#8c8c8c' }}>Non payé</Tag>;
        }

        if (record.subscription_id) {
          return <Tag color="blue">📋 Contrat</Tag>;
        }
        if (isFree) {
          return <Tag color="blue">Gratuit</Tag>;
        }

        if (isSplit) {
          if (record.second_payment_status === 'paid') {
            return <Tag color="success">✓ Payé 100%</Tag>;
          } else if (record.diagnostic_paid) {
            return <Tag color="orange">⏳ Acompte 50% Payé</Tag>;
          } else {
            return <Tag color="warning">⏳ Acompte 50% Attente</Tag>;
          }
        }

        return (
          <Tag color={record.diagnostic_paid ? 'success' : 'warning'}>
            {record.diagnostic_paid ? '✓ Payé' : '⏳ En attente'}
          </Tag>
        );
      }
    },
    {
      title: 'Confirmation',
      dataIndex: 'customer_confirmed',
      key: 'customer_confirmed',
      render: (_: any, record: Intervention) => {
        // Si pas de rapport soumis, ne rien afficher
        if (!record.report_submitted_at) {
          return <Tag color="default">-</Tag>;
        }
        // Contestée
        if (record.customer_confirmed === false && record.customer_rejection_reason) {
          return (
            <Tooltip title={`Raison: ${record.customer_rejection_reason}`}>
              <Tag color="red" style={{ cursor: 'pointer' }}>
                ⚠️ Contestée
              </Tag>
            </Tooltip>
          );
        }
        // Confirmée
        if (record.customer_confirmed === true) {
          return <Tag color="success">✅ Confirmée</Tag>;
        }
        // En attente de confirmation
        return <Tag color="warning">⏳ En attente</Tag>;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record: Intervention) => (
        <Space size="small">
          <Tooltip title="Voir les détails">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewIntervention(record)}
            />
          </Tooltip>
          <Tooltip title="Modifier">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditIntervention(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Confirmer la suppression"
            description="Voulez-vous vraiment supprimer cette intervention ?"
            onConfirm={() => handleDeleteIntervention(record.id)}
            okText="Supprimer"
            cancelText="Annuler"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Supprimer">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Calculer le nombre de contestations
  const disputedCount = interventions.filter(
    i => i.customer_confirmed === false && i.customer_rejection_reason
  ).length;

  const pendingConfirmationCount = interventions.filter(
    i => i.report_submitted_at && i.customer_confirmed !== true && i.customer_confirmed !== false
  ).length;

  // Combiner les clients de l'API avec le client de l'intervention sélectionnée si absent
  const combinedCustomers = [...customers];
  if (selectedIntervention?.customer && selectedIntervention.customer_id) {
    const exists = combinedCustomers.some(c => Number(c.id) === Number(selectedIntervention.customer_id) || (c.user_id && Number(c.user_id) === Number(selectedIntervention.customer_id)));
    if (!exists) {
      combinedCustomers.push({
        id: Number(selectedIntervention.customer_id),
        user_id: Number(selectedIntervention.customer?.user?.id || selectedIntervention.customer_id),
        first_name: selectedIntervention.customer.first_name || '',
        last_name: selectedIntervention.customer.last_name || '',
        createdAt: '',
        updatedAt: ''
      });
    }
  }

  // Combiner les techniciens de l'API avec le technicien de l'intervention sélectionnée si absent
  const combinedTechnicians = [...technicians];
  if (selectedIntervention?.technician && selectedIntervention.technician_id) {
    const exists = combinedTechnicians.some(t => Number(t.id) === Number(selectedIntervention.technician_id) || (t.user_id && Number(t.user_id) === Number(selectedIntervention.technician_id)));
    if (!exists) {
      combinedTechnicians.push({
        id: Number(selectedIntervention.technician_id),
        user_id: Number(selectedIntervention.technician_id),
        first_name: selectedIntervention.technician.first_name || '',
        last_name: selectedIntervention.technician.last_name || ''
      });
    }
  }

  const [exporting, setExporting] = useState<boolean>(false);

  const filteredInterventions = interventions.filter(intervention => {
    // Filtre par recherche texte
    if (searchText && !intervention.title.toLowerCase().includes(searchText.toLowerCase()) &&
        !intervention.description?.toLowerCase().includes(searchText.toLowerCase())) {
      return false;
    }
    // Filtre par type
    if (filterType) {
      const itemType = (intervention.intervention_type || 'maintenance').toLowerCase();
      const itemTitle = (intervention.title || '').toLowerCase();
      const isRepair = ['repair', 'dépannage', 'depannage', 'réparation', 'reparation'].includes(itemType) || itemTitle.includes('dépannage') || itemTitle.includes('depannage') || itemTitle.includes('réparation');
      const isInstallation = itemType === 'installation' || itemTitle.includes('installation');
      const isDiagnostic = itemType === 'diagnostic' || itemTitle.includes('diagnostic');
      const isMaintenance = !isRepair && !isInstallation && !isDiagnostic;

      if (filterType === 'repair' && !isRepair) return false;
      if (filterType === 'maintenance' && !isMaintenance) return false;
      if (filterType === 'installation' && !isInstallation) return false;
      if (filterType === 'diagnostic' && !isDiagnostic) return false;
    }
    // Filtre par statut
    if (filterStatus && intervention.status !== filterStatus) {
      return false;
    }
    // Filtre par priorité
    if (filterPriority && intervention.priority !== filterPriority) {
      return false;
    }
    // Filtre par technicien
    if (filterTechnician !== undefined) {
      if (filterTechnician === 0 && intervention.technician_id) {
        return false;
      }
      if (filterTechnician !== 0 && intervention.technician_id !== filterTechnician) {
        return false;
      }
    }
    // Filtre par confirmation client
    if (filterConfirmation) {
      if (filterConfirmation === 'disputed') {
        // Contestées: customer_confirmed est explicitement false avec une raison
        if (intervention.customer_confirmed !== false || !intervention.customer_rejection_reason) {
          return false;
        }
      } else if (filterConfirmation === 'pending') {
        // En attente: rapport soumis mais pas encore confirmé
        if (!intervention.report_submitted_at || intervention.customer_confirmed === true || intervention.customer_confirmed === false) {
          return false;
        }
      } else if (filterConfirmation === 'confirmed') {
        // Confirmées
        if (intervention.customer_confirmed !== true) {
          return false;
        }
      }
    }
    return true;
  });

  const handleExport = () => {
    try {
      setExporting(true);
      const rows = filteredInterventions.map(item => {
        const cust: any = item.customer || (item.customer_id ? customers.find(c => Number(c.id) === Number(item.customer_id) || Number(c.user_id) === Number(item.customer_id)) : null);
        const custName = cust ? (`${cust.first_name || cust.user?.first_name || ''} ${cust.last_name || cust.user?.last_name || ''}`.trim() || cust.company_name || `Client #${item.customer_id}`) : (item.customer_id ? `Client #${item.customer_id}` : '-');
        const custPhone = cust?.phone || cust?.user?.phone || '';

        const tech: any = item.technician || (item.technician_id ? technicians.find(t => Number(t.id) === Number(item.technician_id)) : null);
        const techName = tech ? (`${tech.first_name || ''} ${tech.last_name || ''}`.trim() || `Technicien #${item.technician_id}`) : (item.technician_id ? `Technicien #${item.technician_id}` : 'Non assigné');

        const typeInfo = interventionTypeMap[item.intervention_type as keyof typeof interventionTypeMap]?.label || item.intervention_type || 'Entretien';
        const statusInfo = statusMap[item.status as keyof typeof statusMap]?.label || item.status;

        return {
          id: item.id,
          titre: item.title,
          type: typeInfo,
          client: custName,
          telephone_client: custPhone,
          technicien: techName,
          statut: statusInfo,
          priorite: item.priority === 'critical' ? 'Critique' : item.priority === 'high' ? 'Haute' : item.priority === 'medium' ? 'Moyenne' : 'Basse',
          date_planifiee: item.scheduled_date ? dayjs(item.scheduled_date).format('DD/MM/YYYY HH:mm') : '-',
          adresse: item.address || (item as any).location_address || cust?.address || '-'
        };
      });

      exportToCSV(
        rows,
        [
          { header: 'N°', key: 'id' },
          { header: 'Titre', key: 'titre' },
          { header: 'Type', key: 'type' },
          { header: 'Client', key: 'client' },
          { header: 'Téléphone Client', key: 'telephone_client' },
          { header: 'Technicien', key: 'technicien' },
          { header: 'Statut', key: 'statut' },
          { header: 'Priorité', key: 'priorite' },
          { header: 'Date Planifiée', key: 'date_planifiee' },
          { header: 'Adresse', key: 'adresse' },
        ],
        `interventions_${new Date().toISOString().slice(0, 10)}.csv`
      );
      message.success('Export des interventions généré avec succès');
    } catch (err) {
      console.error('Erreur export interventions:', err);
      message.error("Erreur lors de l'export des interventions");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Alerte contestations */}
      {disputedCount > 0 && (
        <div style={{ 
          marginBottom: '16px', 
          padding: '12px 16px', 
          background: '#fff1f0', 
          border: '1px solid #ffa39e',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '24px' }}>⚠️</span>
            <div>
              <strong style={{ color: '#cf1322' }}>
                {disputedCount} intervention{disputedCount > 1 ? 's' : ''} contestée{disputedCount > 1 ? 's' : ''}
              </strong>
              <p style={{ margin: 0, color: '#a8071a', fontSize: '13px' }}>
                Des clients ont signalé des problèmes avec ces interventions
              </p>
            </div>
          </div>
          <Button 
            type="primary" 
            danger
            onClick={() => setFilterConfirmation('disputed')}
          >
            Voir les contestations
          </Button>
        </div>
      )}

      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
            🛠️ Gestion des Interventions
          </Title>
          <p style={{ color: '#666', marginTop: '8px', marginBottom: 0 }}>
            {interventions.length} intervention{interventions.length > 1 ? 's' : ''} enregistrée{interventions.length > 1 ? 's' : ''}
            {pendingConfirmationCount > 0 && (
              <span style={{ marginLeft: '12px', color: '#d48806' }}>
                • {pendingConfirmationCount} en attente de confirmation
              </span>
            )}
          </p>
        </Col>
        <Col>
          <Space size="middle">
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              loading={exporting}
            >
              Exporter
            </Button>
            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={loadInterventions}
              loading={loading}
            >
              Actualiser
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddIntervention}
              size="large"
              style={{ 
                background: '#0a543d',
                borderColor: '#0a543d',
                height: '42px',
                fontWeight: 'bold'
              }}
            >
              Nouvelle Demande
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Filtres */}
      <Card style={{ marginBottom: '16px', borderRadius: '8px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="🔍 Rechercher..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              size="large"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="📋 Statut"
              value={filterStatus}
              onChange={setFilterStatus}
              allowClear
              style={{ width: '100%' }}
              size="large"
            >
              {Object.entries(statusMap).map(([key, { label }]) => (
                <Select.Option key={key} value={key}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="⚡ Priorité"
              value={filterPriority}
              onChange={setFilterPriority}
              allowClear
              style={{ width: '100%' }}
              size="large"
            >
              {Object.entries(priorityMap).map(([key, { label }]) => (
                <Select.Option key={key} value={key}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filtrer par technicien"
              value={filterTechnician}
              onChange={setFilterTechnician}
              allowClear
              showSearch
              style={{ width: '100%' }}
              size="large"
              filterOption={(input, option) => {
                const tech = technicians.find(t => t.id === option?.value);
                return tech ? 
                  `${tech.first_name} ${tech.last_name}`.toLowerCase().includes(input.toLowerCase()) : 
                  false;
              }}
            >
              <Select.Option value={0}>Non assigné</Select.Option>
              {technicians.map((tech) => (
                <Select.Option key={tech.id} value={tech.id}>
                  {tech.first_name} {tech.last_name} {tech.availability_status === 'available' ? '🟢 (En ligne)' : '⚪ (Hors ligne)'}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="🏷️ Type d'intervention"
              value={filterType}
              onChange={setFilterType}
              allowClear
              style={{ width: '100%' }}
              size="large"
            >
              <Select.Option value="maintenance">🔧 Entretien / Maintenance</Select.Option>
              <Select.Option value="repair">🛠️ Dépannage / Réparation</Select.Option>
              <Select.Option value="installation">❄️ Installation</Select.Option>
              <Select.Option value="diagnostic">🔍 Diagnostic</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="🔔 Confirmation client"
              value={filterConfirmation}
              onChange={setFilterConfirmation}
              allowClear
              style={{ width: '100%' }}
              size="large"
            >
              <Select.Option value="disputed">
                <span style={{ color: 'red' }}>⚠️ Contestées</span>
              </Select.Option>
              <Select.Option value="pending">
                <span style={{ color: 'orange' }}>⏳ En attente de confirmation</span>
              </Select.Option>
              <Select.Option value="confirmed">
                <span style={{ color: 'green' }}>✅ Confirmées</span>
              </Select.Option>
            </Select>
          </Col>
        </Row>
      </Card>

      <Card style={{ borderRadius: '8px' }}>
        <Table
          columns={columns}
          dataSource={filteredInterventions}
          rowKey="id"
          rowClassName={(record) => record.status === 'cancelled' ? 'cancelled-row' : ''}
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} interventions`,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title={editMode ? 'Modifier l\'intervention' : 'Nouvelle intervention'}
        open={openModal}
        onCancel={() => setOpenModal(false)}
        onOk={handleSubmit}
        okText={editMode ? 'Enregistrer' : 'Créer'}
        cancelText="Annuler"
        width={600}
        destroyOnClose
      >
        <div style={{ paddingTop: '16px' }}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                Titre *
              </label>
              <Input
                placeholder="Titre de l'intervention"
                value={newIntervention.title}
                onChange={e => setNewIntervention({ ...newIntervention, title: e.target.value })}
                size="large"
              />
            </Col>
            <Col span={newIntervention.intervention_type === 'installation' ? 12 : 24}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                Type d'intervention *
              </label>
              <Select
                placeholder="Sélectionner le type d'intervention"
                value={newIntervention.intervention_type}
                onChange={value => setNewIntervention({ ...newIntervention, intervention_type: value })}
                style={{ width: '100%' }}
                size="large"
              >
                <Select.Option value="maintenance">🔧 Entretien / Maintenance</Select.Option>
                <Select.Option value="repair">🛠️ Dépannage / Réparation</Select.Option>
                <Select.Option value="installation">❄️ Installation</Select.Option>
                <Select.Option value="diagnostic">🔍 Diagnostic</Select.Option>
              </Select>
            </Col>
            {newIntervention.intervention_type === 'installation' && (
              <Col span={12}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Type de climatiseur
                </label>
                <Select
                  placeholder="Type de climatiseur"
                  value={newIntervention.climatiseur_type}
                  onChange={value => setNewIntervention({ ...newIntervention, climatiseur_type: value })}
                  style={{ width: '100%' }}
                  size="large"
                  allowClear
                >
                  <Select.Option value="Mural">Mural</Select.Option>
                  <Select.Option value="Allège">Allège</Select.Option>
                  <Select.Option value="K7">K7 (Cassette)</Select.Option>
                  <Select.Option value="Gainable">Gainable</Select.Option>
                  <Select.Option value="Armoire">Armoire</Select.Option>
                </Select>
              </Col>
            )}
            <Col span={24}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                Description
              </label>
              <Input.TextArea
                placeholder="Description détaillée de l'intervention"
                value={newIntervention.description}
                onChange={e => setNewIntervention({ ...newIntervention, description: e.target.value })}
                rows={3}
              />
            </Col>
            <Col span={12}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                Client *
              </label>
              <Select
                placeholder="Sélectionner un client"
                value={newIntervention.customer_id ? Number(newIntervention.customer_id) : undefined}
                onChange={value => {
                  const numVal = value ? Number(value) : undefined;
                  const selectedCust = numVal ? combinedCustomers.find(c => Number(c.id) === numVal || (c.user_id && Number(c.user_id) === numVal)) : null;
                  const newCity = selectedCust?.city || (selectedCust?.commune && !['Grand-Bassam', 'Assinie', 'Songon', 'Anyama', 'Bingerville'].includes(selectedCust.commune) ? 'Abidjan' : selectedCust?.commune) || newIntervention.city || 'Abidjan';
                  const newCommune = selectedCust?.commune || (newCity === 'Abidjan' ? newIntervention.commune : undefined);
                  const newAddress = selectedCust?.address || newIntervention.address || '';
                  setNewIntervention({
                    ...newIntervention,
                    customer_id: numVal,
                    city: newCity,
                    commune: newCommune,
                    address: newAddress
                  });
                }}
                style={{ width: '100%' }}
                size="large"
                showSearch
                optionLabelProp="label"
                filterOption={(input, option) => {
                  const label = option?.label as string || '';
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {combinedCustomers.map((customer) => {
                  const displayName = `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || customer.company_name || `Client #${customer.id}`;
                  return (
                    <Select.Option 
                      key={`cp-${customer.id}`} 
                      value={Number(customer.id)}
                      label={displayName}
                    >
                      {displayName}
                    </Select.Option>
                  );
                })}
              </Select>
            </Col>
            <Col span={12}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                Technicien
              </label>
              <Select
                placeholder="Sélectionner un technicien"
                value={newIntervention.technician_id ? Number(newIntervention.technician_id) : undefined}
                onChange={value => setNewIntervention({ ...newIntervention, technician_id: value ? Number(value) : undefined })}
                style={{ width: '100%' }}
                size="large"
                allowClear
                showSearch
                optionLabelProp="label"
                filterOption={(input, option) => {
                  const label = option?.label as string || '';
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {combinedTechnicians.flatMap((technician) => {
                  const opts = [
                    <Select.Option 
                      key={`tech-${technician.id}`} 
                      value={Number(technician.id)}
                      label={`${technician.first_name} ${technician.last_name}`.trim()}
                    >
                      {technician.first_name} {technician.last_name} {technician.availability_status === 'available' ? '🟢 (En ligne)' : '⚪ (Hors ligne)'}
                    </Select.Option>
                  ];
                  if (technician.user_id && Number(technician.user_id) !== Number(technician.id)) {
                    opts.push(
                      <Select.Option 
                        key={`techusr-${technician.user_id}`} 
                        value={Number(technician.user_id)}
                        label={`${technician.first_name} ${technician.last_name}`.trim()}
                      >
                        {technician.first_name} {technician.last_name} {technician.availability_status === 'available' ? '🟢 (En ligne)' : '⚪ (Hors ligne)'}
                      </Select.Option>
                    );
                  }
                  return opts;
                })}
              </Select>
            </Col>
            <Col span={12}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                Ville du client
              </label>
              <Select
                placeholder="Sélectionner la ville"
                value={newIntervention.city}
                onChange={val => {
                  setNewIntervention({
                    ...newIntervention,
                    city: val,
                    commune: val === 'Abidjan' ? newIntervention.commune : undefined
                  });
                }}
                style={{ width: '100%' }}
                size="large"
                showSearch
              >
                {OPERATIONAL_CITIES.map(city => (
                  <Select.Option key={city} value={city}>{city}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col span={12}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                Commune {newIntervention.city === 'Abidjan' ? '*' : ''}
              </label>
              <Select
                placeholder={newIntervention.city === 'Abidjan' ? 'Sélectionner la commune' : 'Non applicable hors Abidjan'}
                value={newIntervention.city === 'Abidjan' ? newIntervention.commune : undefined}
                disabled={newIntervention.city !== 'Abidjan'}
                onChange={val => setNewIntervention({ ...newIntervention, commune: val })}
                style={{ width: '100%' }}
                size="large"
                showSearch
                allowClear
              >
                {ABIDJAN_COMMUNES.map(commune => (
                  <Select.Option key={commune} value={commune}>{commune}</Select.Option>
                ))}
              </Select>
              {newIntervention.city && newIntervention.city !== 'Abidjan' && (
                <span style={{ fontSize: '11px', color: '#94A3B8', marginTop: '2px', display: 'block' }}>
                  La sélection de commune est réservée à Abidjan
                </span>
              )}
            </Col>
            <Col span={24}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                Adresse / Repère géographique
              </label>
              <Input
                placeholder="Ex: Quartier, rue, numéro de porte, villa ou repère précis..."
                prefix={<EnvironmentOutlined style={{ color: '#0a543d' }} />}
                value={newIntervention.address}
                onChange={e => setNewIntervention({ ...newIntervention, address: e.target.value })}
                size="large"
              />
            </Col>
            <Col span={12}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                Date prévue
              </label>
              <DatePicker
                value={newIntervention.scheduled_date}
                onChange={date => setNewIntervention({ ...newIntervention, scheduled_date: date })}
                format="DD/MM/YYYY HH:mm"
                showTime
                style={{ width: '100%' }}
                size="large"
                placeholder="Sélectionner la date"
              />
            </Col>
            <Col span={12}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                Priorité
              </label>
              <Select
                value={newIntervention.priority}
                onChange={value => setNewIntervention({ ...newIntervention, priority: value })}
                style={{ width: '100%' }}
                size="large"
              >
                {Object.entries(priorityMap).map(([value, { label }]) => (
                  <Select.Option key={value} value={value}>{label}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col span={12}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                Nombre d'équipements *
              </label>
              <Input
                type="number"
                min={1}
                placeholder="Nombre d'équipements"
                value={newIntervention.equipment_count}
                onChange={e => setNewIntervention({ ...newIntervention, equipment_count: parseInt(e.target.value) || 1 })}
                size="large"
              />
            </Col>
            {editMode && (
              <Col span={12}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Statut
                </label>
                <Select
                  value={newIntervention.status}
                  onChange={value => setNewIntervention({ ...newIntervention, status: value })}
                  style={{ width: '100%' }}
                  size="large"
                >
                  {Object.entries(statusMap).map(([value, { label }]) => (
                    <Select.Option key={value} value={value}>{label}</Select.Option>
                  ))}
                </Select>
              </Col>
            )}
          </Row>
        </div>
      </Modal>

      <Modal
        title="Détails de l'intervention"
        open={viewModal}
        onCancel={() => setViewModal(false)}
        footer={[
          <Button key="close" onClick={() => setViewModal(false)}>
            Fermer
          </Button>,
          selectedIntervention && selectedIntervention.status === 'client_unreachable' && (
            <Button
              key="reschedule"
              type="primary"
              danger
              icon={<CalendarOutlined />}
              onClick={() => setRescheduleModal(true)}
            >
              Reprogrammer
            </Button>
          ),
          selectedIntervention && selectedIntervention.refund_status === 'pending' && (
            <Button
              key="check-refund"
              icon={<SyncOutlined spin={checkingRefund} />}
              loading={checkingRefund}
              onClick={() => handleCheckRefundStatus(selectedIntervention.id)}
            >
              Vérifier le statut FineoPay
            </Button>
          ),
          isSuperAdmin && selectedIntervention && selectedIntervention.status === 'cancelled' && selectedIntervention.diagnostic_paid && selectedIntervention.refund_status !== 'refunded' && selectedIntervention.refund_status !== 'pending' && (
            <Button
              key="refund"
              type="primary"
              danger
              icon={<DollarOutlined />}
              onClick={() => {
                const initialAmount = Number(selectedIntervention.diagnostic_fee || selectedIntervention.total_price || 0);
                const initialPhone = selectedIntervention.customer?.user?.phone || selectedIntervention.customer?.phone || '';
                setRefundAmount(initialAmount);
                setRefundPhone(initialPhone);
                setRefundCanal(selectedIntervention.refund_canal || 'wave');
                setRefundReason(`Remboursement intervention #${selectedIntervention.id} annulée`);
                setRefundModal(true);
              }}
            >
              {selectedIntervention.refund_status === 'failed' ? 'Retenter le remboursement' : 'Rembourser le client'}
            </Button>
          ),
          selectedIntervention && !selectedIntervention.technician_id && (
            <Button
              key="suggestions"
              type="primary"
              icon={<UserOutlined />}
              onClick={() => {
                setSuggestionsModal(true);
              }}
            >
              Suggérer des techniciens
            </Button>
          )
        ]}
        width={700}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        {selectedIntervention && (
          <div style={{ paddingTop: '16px' }}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Title level={4} style={{ marginBottom: '16px' }}>
                  {selectedIntervention.title}
                </Title>
              </Col>
              <Col span={12}>
                <strong>Statut:</strong>
                <div style={{ marginTop: '4px' }}>
                  <Tag color={statusMap[selectedIntervention.status as keyof typeof statusMap]?.color}>
                    {statusMap[selectedIntervention.status as keyof typeof statusMap]?.label || selectedIntervention.status}
                  </Tag>
                </div>
              </Col>
              <Col span={12}>
                <strong>Priorité:</strong>
                <div style={{ marginTop: '4px' }}>
                  <Tag color={priorityMap[selectedIntervention.priority as keyof typeof priorityMap]?.color}>
                    {priorityMap[selectedIntervention.priority as keyof typeof priorityMap]?.label || selectedIntervention.priority}
                  </Tag>
                </div>
              </Col>
              <Col span={12}>
                <strong>Date prévue:</strong>
                <div style={{ marginTop: '4px' }}>
                  {selectedIntervention.scheduled_date ? 
                    dayjs(selectedIntervention.scheduled_date).format('DD/MM/YYYY HH:mm') : 
                    'Non définie'
                  }
                </div>
              </Col>
              <Col span={12}>
                <strong>Client:</strong>
                <div style={{ marginTop: '4px' }}>
                  {selectedIntervention.customer ? 
                    `${selectedIntervention.customer.first_name} ${selectedIntervention.customer.last_name}` : 
                    'Non défini'
                  }
                </div>
              </Col>
              <Col span={12}>
                <strong>Technicien:</strong>
                <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {selectedIntervention.technician ? (
                    <>
                      <UserAvatar
                        src={selectedIntervention.technician.profile_image || selectedIntervention.technician.profile_picture}
                        firstName={selectedIntervention.technician.first_name}
                        lastName={selectedIntervention.technician.last_name}
                        size={32}
                      />
                      <span>{`${selectedIntervention.technician.first_name} ${selectedIntervention.technician.last_name}`}</span>
                    </>
                  ) : (
                    'Non assigné'
                  )}
                </div>
              </Col>
              <Col span={12}>
                <strong>Créé le:</strong>
                <div style={{ marginTop: '4px' }}>
                  {dayjs(selectedIntervention.created_at).format('DD/MM/YYYY HH:mm')}
                </div>
              </Col>
              <Col span={12}>
                <strong>Nombre d'équipements:</strong>
                <div style={{ marginTop: '4px' }}>
                  {selectedIntervention.equipment_count || 1}
                </div>
              </Col>
              <Col span={12}>
                <strong>Localisation & Adresse:</strong>
                <div style={{ marginTop: '4px', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                  <EnvironmentOutlined style={{ color: '#0a543d', marginTop: '3px' }} />
                  <span>
                    {selectedIntervention.address || (selectedIntervention as any).location_address || [
                      (selectedIntervention.customer as any)?.commune,
                      (selectedIntervention.customer as any)?.city,
                      (selectedIntervention.customer as any)?.address
                    ].filter(Boolean).join(', ') || 'Non spécifiée'}
                  </span>
                </div>
              </Col>
              {/* Block de paiement pour l'intervention */}
              <Col span={24}>
                {(() => {
                  const isFreeDiag = selectedIntervention.is_free_diagnosis || (!selectedIntervention.diagnostic_fee && !selectedIntervention.total_price) || Number(selectedIntervention.diagnostic_fee) === 0;
                  const isSplit = selectedIntervention.payment_option === 'split' && selectedIntervention.second_payment_amount && Number(selectedIntervention.second_payment_amount) > 0 && !isFreeDiag;
                  
                  // 1) Si l'intervention est gratuite
                  if (isFreeDiag) {
                    return (
                      <div style={{ 
                        marginTop: '8px', 
                        padding: '12px', 
                        background: '#e6f7ff', 
                        borderRadius: '6px',
                        border: '1px solid #91d5ff'
                      }}>
                        <strong>💳 Paiement de l'intervention:</strong>
                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <Tag color="blue">✓ Gratuit</Tag>
                          <span>Montant: <strong>0 F CFA (Gratuit)</strong></span>
                        </div>
                      </div>
                    );
                  }

                  // 2) Libellé du paiement initial / acompte
                  let label = '💳 Paiement:';
                  if (selectedIntervention.intervention_type === 'maintenance') {
                    label = isSplit ? '💳 Premier acompte maintenance (50%):' : '💳 Paiement maintenance (100%):';
                  } else if (selectedIntervention.intervention_type === 'repair') {
                    label = isSplit ? '💳 Premier acompte dépannage (50%):' : '💳 Paiement dépannage (100%):';
                  } else if (selectedIntervention.intervention_type === 'installation') {
                    label = isSplit ? '💳 Premier acompte installation (50%):' : '💳 Paiement installation (100%):';
                  } else if (isSplit) {
                    label = '💳 Premier acompte (50%):';
                  } else {
                    label = '💳 Paiement intégral (100%):';
                  }

                  const firstPaid = selectedIntervention.diagnostic_paid === true;
                  const feeAmount = selectedIntervention.diagnostic_fee || selectedIntervention.total_price || 0;
                  const secondPaid = selectedIntervention.second_payment_status === 'paid';
                  const secondAmount = Number(selectedIntervention.second_payment_amount || 0);

                  return (
                    <>
                      {/* Box 1 : Paiement 100% ou Acompte 50% */}
                      <div style={{ 
                        marginTop: '8px', 
                        padding: '12px', 
                        background: firstPaid ? '#f6ffed' : '#fff7e6', 
                        borderRadius: '6px',
                        border: `1px solid ${firstPaid ? '#b7eb8f' : '#ffd591'}`
                      }}>
                        <strong>{label}</strong>
                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <Tag color={firstPaid ? 'success' : 'warning'}>
                            {firstPaid ? (isSplit ? '✓ Acompte Payé' : '✓ Payé à 100%') : (isSplit ? '⏳ Acompte en attente' : '⏳ En attente de paiement')}
                          </Tag>
                          <span>Montant: <strong>{`${feeAmount.toLocaleString()} F CFA`}</strong></span>
                          {firstPaid && selectedIntervention.diagnostic_payment_date && (
                            <span style={{ color: '#52c41a' }}>
                              (Payé le {dayjs(selectedIntervention.diagnostic_payment_date).format('DD/MM/YYYY')})
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Box 2 : Solde 50% restant (UNIQUEMENT si paiement échelonné split et solde > 0) */}
                      {isSplit && (
                        <div style={{ 
                          marginTop: '8px', 
                          padding: '12px', 
                          background: secondPaid ? '#f6ffed' : '#fff7e6', 
                          borderRadius: '6px',
                          border: `1px solid ${secondPaid ? '#b7eb8f' : '#ffd591'}`
                        }}>
                          <strong>💳 Paiement du solde (50% restant):</strong>
                          <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Tag color={secondPaid ? 'success' : 'warning'}>
                              {secondPaid ? '✓ Solde Payé' : '⏳ En attente (Solde fin d\'intervention)'}
                            </Tag>
                            <span>Montant: <strong>{`${secondAmount.toLocaleString()} F CFA`}</strong></span>
                            {secondPaid && selectedIntervention.second_payment_date && (
                              <span style={{ color: '#52c41a' }}>
                                (Payé le {dayjs(selectedIntervention.second_payment_date).format('DD/MM/YYYY')})
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Box 3 : Remboursement si intervention annulée */}
                      {selectedIntervention.status === 'cancelled' && (firstPaid || Boolean(selectedIntervention.refund_status)) && (
                        <div style={{
                          marginTop: '12px',
                          padding: '14px',
                          background: selectedIntervention.refund_status === 'refunded'
                            ? '#f6ffed'
                            : selectedIntervention.refund_status === 'pending'
                              ? '#fffbe6'
                              : selectedIntervention.refund_status === 'failed'
                                ? '#fff1f0'
                                : '#fff2e8',
                          borderRadius: '8px',
                          border: `1px solid ${
                            selectedIntervention.refund_status === 'refunded'
                              ? '#b7eb8f'
                              : selectedIntervention.refund_status === 'pending'
                                ? '#ffe58f'
                                : selectedIntervention.refund_status === 'failed'
                                  ? '#ffa39e'
                                  : '#ffbb96'
                          }`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: '12px'
                        }}>
                          <div style={{ flex: 1, minWidth: '260px' }}>
                            {selectedIntervention.refund_status === 'refunded' && (
                              <>
                                <strong style={{ color: '#389e0d', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <CheckCircleOutlined /> Remboursement effectué (FineoPay Payout)
                                </strong>
                                <div style={{ marginTop: '4px', fontSize: '13px', color: '#595959' }}>
                                  Montant : <strong>{Number(selectedIntervention.refund_amount || 0).toLocaleString()} F CFA</strong>
                                  {selectedIntervention.refund_canal && ` via ${selectedIntervention.refund_canal.toUpperCase()}`}
                                  {selectedIntervention.refund_phone && ` (${selectedIntervention.refund_phone})`}
                                  {selectedIntervention.refund_date && ` • le ${dayjs(selectedIntervention.refund_date).format('DD/MM/YYYY HH:mm')}`}
                                </div>
                              </>
                            )}
                            {selectedIntervention.refund_status === 'pending' && (
                              <>
                                <strong style={{ color: '#d48806', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <SyncOutlined spin /> Remboursement en cours de traitement (FineoPay Payout)
                                </strong>
                                <div style={{ marginTop: '4px', fontSize: '13px', color: '#595959' }}>
                                  Montant : <strong>{Number(selectedIntervention.refund_amount || 0).toLocaleString()} F CFA</strong>
                                  {selectedIntervention.refund_canal && ` via ${selectedIntervention.refund_canal.toUpperCase()}`}
                                  {selectedIntervention.refund_phone && ` (${selectedIntervention.refund_phone})`}
                                  {selectedIntervention.refund_reference && ` • Réf : ${selectedIntervention.refund_reference}`}
                                </div>
                                <div style={{ marginTop: '4px', fontSize: '12px', color: '#8c8c8c' }}>
                                  Le virement est en file d'attente chez FineoPay / opérateur. Vous pouvez vérifier son statut en direct.
                                </div>
                              </>
                            )}
                            {selectedIntervention.refund_status === 'failed' && (
                              <>
                                <strong style={{ color: '#cf1322', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <CloseCircleOutlined /> Échec du remboursement FineoPay
                                </strong>
                                <div style={{ marginTop: '4px', fontSize: '13px', color: '#595959' }}>
                                  Le virement de <strong>{Number(selectedIntervention.refund_amount || feeAmount).toLocaleString()} F CFA</strong> a été rejeté par l'opérateur ou FineoPay.
                                  {selectedIntervention.refund_phone && ` (Cible : ${selectedIntervention.refund_phone})`}
                                </div>
                                <div style={{ marginTop: '4px', fontSize: '12px', color: '#cf1322' }}>
                                  L'argent n'a pas quitté votre compte. Vous pouvez re-vérifier ou relancer le virement.
                                </div>
                              </>
                            )}
                            {!selectedIntervention.refund_status && (
                              <>
                                <strong style={{ color: '#d4380d', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <DollarOutlined /> Intervention annulée avec paiement perçu
                                </strong>
                                <div style={{ marginTop: '4px', fontSize: '13px', color: '#595959' }}>
                                  Le client a réglé <strong>{feeAmount.toLocaleString()} F CFA</strong>. Vous pouvez effectuer le remboursement direct par Mobile Money.
                                </div>
                              </>
                            )}
                          </div>
                          <div>
                            {selectedIntervention.refund_status === 'refunded' && (
                              <Tag color="success" style={{ padding: '4px 10px', fontSize: '13px' }}>
                                ✓ Remboursé
                              </Tag>
                            )}
                            {selectedIntervention.refund_status === 'pending' && (
                              <Space>
                                <Tag color="processing" style={{ padding: '4px 10px', fontSize: '13px' }}>
                                  ⏳ En cours
                                </Tag>
                                <Button
                                  type="primary"
                                  ghost
                                  size="small"
                                  icon={<SyncOutlined spin={checkingRefund} />}
                                  loading={checkingRefund}
                                  onClick={() => handleCheckRefundStatus(selectedIntervention.id)}
                                >
                                  Vérifier le statut
                                </Button>
                              </Space>
                            )}
                            {selectedIntervention.refund_status === 'failed' && (
                              <Space>
                                <Tag color="error" style={{ padding: '4px 10px', fontSize: '13px' }}>
                                  ⚠️ Échec
                                </Tag>
                                <Button
                                  size="small"
                                  icon={<SyncOutlined spin={checkingRefund} />}
                                  loading={checkingRefund}
                                  onClick={() => handleCheckRefundStatus(selectedIntervention.id)}
                                >
                                  Re-vérifier
                                </Button>
                                {isSuperAdmin && (
                                  <Button
                                    type="primary"
                                    danger
                                    size="small"
                                    icon={<DollarOutlined />}
                                    onClick={() => {
                                      const initialAmount = Number(selectedIntervention.refund_amount || selectedIntervention.diagnostic_fee || selectedIntervention.total_price || 0);
                                      const initialPhone = selectedIntervention.refund_phone || selectedIntervention.customer?.user?.phone || selectedIntervention.customer?.phone || '';
                                      setRefundAmount(initialAmount);
                                      setRefundPhone(initialPhone);
                                      setRefundCanal(selectedIntervention.refund_canal || 'wave');
                                      setRefundReason(`Remboursement intervention #${selectedIntervention.id} annulée`);
                                      setRefundModal(true);
                                    }}
                                  >
                                    Retenter
                                  </Button>
                                )}
                              </Space>
                            )}
                            {!selectedIntervention.refund_status && (
                              isSuperAdmin ? (
                                <Button
                                  type="primary"
                                  danger
                                  icon={<DollarOutlined />}
                                  onClick={() => {
                                    const initialAmount = Number(selectedIntervention.diagnostic_fee || selectedIntervention.total_price || 0);
                                    const initialPhone = selectedIntervention.customer?.user?.phone || selectedIntervention.customer?.phone || '';
                                    setRefundAmount(initialAmount);
                                    setRefundPhone(initialPhone);
                                    setRefundCanal('wave');
                                    setRefundReason(`Remboursement intervention #${selectedIntervention.id} annulée`);
                                    setRefundModal(true);
                                  }}
                                >
                                  Rembourser le client
                                </Button>
                              ) : (
                                <Tag color="orange" style={{ padding: '6px 12px', fontSize: '12px' }}>
                                  🔒 Réservé au Super Administrateur
                                </Tag>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </Col>
              {/* Confirmation client */}
              {selectedIntervention.report_submitted_at && (
                <Col span={24}>
                  <div style={{ 
                    marginTop: '8px', 
                    padding: '12px', 
                    background: selectedIntervention.customer_confirmed === true 
                      ? '#f6ffed' 
                      : selectedIntervention.customer_confirmed === false 
                        ? '#fff1f0' 
                        : '#fffbe6', 
                    borderRadius: '6px',
                    border: `1px solid ${selectedIntervention.customer_confirmed === true 
                      ? '#b7eb8f' 
                      : selectedIntervention.customer_confirmed === false 
                        ? '#ffa39e' 
                        : '#ffe58f'}`
                  }}>
                    <strong>🔔 Confirmation client:</strong>
                    <div style={{ marginTop: '8px' }}>
                      {selectedIntervention.customer_confirmed === true ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <Tag color="success">✅ Confirmée</Tag>
                          {selectedIntervention.customer_confirmed_at && (
                            <span style={{ color: '#52c41a' }}>
                              (Confirmé le {dayjs(selectedIntervention.customer_confirmed_at).format('DD/MM/YYYY à HH:mm')})
                            </span>
                          )}
                        </div>
                      ) : selectedIntervention.customer_confirmed === false && selectedIntervention.customer_rejection_reason ? (
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                            <Tag color="error">⚠️ Contestée</Tag>
                            {selectedIntervention.customer_confirmed_at && (
                              <span style={{ color: '#cf1322' }}>
                                (le {dayjs(selectedIntervention.customer_confirmed_at).format('DD/MM/YYYY à HH:mm')})
                              </span>
                            )}
                          </div>
                          <div style={{ 
                            padding: '10px', 
                            background: '#fff', 
                            borderRadius: '4px', 
                            border: '1px solid #ffa39e' 
                          }}>
                            <strong>Raison de la contestation:</strong>
                            <p style={{ margin: '8px 0 0 0', color: '#cf1322' }}>
                              {selectedIntervention.customer_rejection_reason}
                            </p>
                          </div>
                          <Button 
                            type="primary" 
                            style={{ marginTop: '12px' }}
                            onClick={() => handleResolveDispute(selectedIntervention.id)}
                          >
                            ✓ Marquer comme traitée
                          </Button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <Tag color="warning">⏳ En attente de confirmation</Tag>
                          <span style={{ color: '#d48806' }}>
                            Rapport soumis le {dayjs(selectedIntervention.report_submitted_at).format('DD/MM/YYYY à HH:mm')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              )}
              {selectedIntervention.description && (
                <Col span={24}>
                  <strong>Description:</strong>
                  <div style={{ 
                    marginTop: '8px', 
                    padding: '12px', 
                    background: '#f5f5f5', 
                    borderRadius: '6px',
                    border: '1px solid #d9d9d9'
                  }}>
                    {selectedIntervention.description}
                  </div>
                </Col>
              )}
              {/* Photos du client */}
              {selectedIntervention.intervention_images && selectedIntervention.intervention_images.length > 0 && (
                <Col span={24} style={{ marginTop: '16px', padding: '16px', background: '#e6f7ff', borderRadius: '8px' }}>
                  <strong style={{ fontSize: '16px' }}>📷 Photos du client ({selectedIntervention.intervention_images.length}):</strong>
                  <div style={{ marginTop: '12px' }}>
                    <Image.PreviewGroup>
                      <Space size={12} wrap>
                        {selectedIntervention.intervention_images
                          .sort((a: any, b: any) => a.order - b.order)
                          .map((image: any) => {
                            const url = getImageUrl(image.image_url);
                            console.log('🖼️ Client Image:', url);
                            const isVideo = url.match(/\.(mp4|mov|avi)$/i);
                            return isVideo ? (
                              <video
                                key={image.id}
                                src={url}
                                controls
                                style={{
                                  width: 200,
                                  height: 120,
                                  objectFit: 'contain',
                                  borderRadius: '8px',
                                  border: '2px solid #1890ff',
                                  backgroundColor: '#000'
                                }}
                              />
                            ) : (
                            <Image
                              key={image.id}
                              src={url}
                              alt={`Photo client ${image.order + 1}`}
                              width={120}
                              height={120}
                              style={{ 
                                objectFit: 'cover', 
                                borderRadius: '8px',
                                cursor: 'pointer',
                                border: '2px solid #1890ff'
                              }}
                              placeholder={
                                <div style={{
                                  width: 120,
                                  height: 120,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: '#f5f5f5'
                                }}>
                                  Chargement...
                                </div>
                              }
                              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=="
                              onError={(e) => console.error('❌ Erreur chargement image:', url, e)}
                            />
                          );
                          })}
                      </Space>
                    </Image.PreviewGroup>
                  </div>
                </Col>
              )}

              {/* Photos du rapport technicien */}
              {selectedIntervention.report_images && selectedIntervention.report_images.length > 0 && (
                <Col span={24} style={{ marginTop: '16px', padding: '16px', background: '#f6ffed', borderRadius: '8px' }}>
                  <strong style={{ fontSize: '16px' }}>📸 Photos du rapport technicien ({selectedIntervention.report_images.length}):</strong>
                  <div style={{ marginTop: '12px' }}>
                    <Image.PreviewGroup>
                      <Space size={12} wrap>
                        {selectedIntervention.report_images
                          .sort((a: any, b: any) => a.order - b.order)
                          .map((image: any) => {
                            const url = getImageUrl(image.image_url);
                            console.log('🖼️ Report Image:', url);
                            const isVideo = url.match(/\.(mp4|mov|avi)$/i);
                            return isVideo ? (
                              <video
                                key={image.id}
                                src={url}
                                controls
                                style={{
                                  width: 200,
                                  height: 120,
                                  objectFit: 'contain',
                                  borderRadius: '8px',
                                  border: '2px solid #52c41a',
                                  backgroundColor: '#000'
                                }}
                              />
                            ) : (
                            <Image
                              key={image.id}
                              src={url}
                              alt={`Photo rapport ${image.order + 1}`}
                              width={120}
                              height={120}
                              style={{ 
                                objectFit: 'cover', 
                                borderRadius: '8px',
                                cursor: 'pointer',
                                border: '2px solid #52c41a'
                              }}
                              placeholder={
                                <div style={{
                                  width: 120,
                                  height: 120,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: '#f5f5f5'
                                }}>
                                  Chargement...
                                </div>
                              }
                              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=="
                              onError={(e) => console.error('❌ Erreur chargement image:', url, e)}
                            />
                          );
                          })}
                      </Space>
                    </Image.PreviewGroup>
                  </div>
                </Col>
              )}
            </Row>
          </div>
        )}
      </Modal>

      {/* Modal des suggestions de techniciens */}
      {selectedIntervention && (
        <TechnicianSuggestionsModal
          visible={suggestionsModal}
          interventionId={selectedIntervention.id}
          interventionTitle={selectedIntervention.title}
          onClose={() => setSuggestionsModal(false)}
          onAssigned={() => {
            loadInterventions();
            setViewModal(false);
          }}
        />
      )}

      <Modal
        title="Reprogrammer l'intervention"
        open={rescheduleModal}
        onCancel={() => setRescheduleModal(false)}
        onOk={handleReschedule}
        okText="Reprogrammer"
        cancelText="Annuler"
      >
        <div style={{ padding: '16px 0' }}>
          <p style={{ marginBottom: 16 }}>Veuillez sélectionner la nouvelle date et heure pour l'intervention <strong>{selectedIntervention?.title}</strong> :</p>
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            style={{ width: '100%' }}
            value={rescheduleDate}
            onChange={(date) => setRescheduleDate(date)}
          />
        </div>
      </Modal>

      {/* Modal de Remboursement Mobile Money (FineoPay Payout) */}
      <Modal
        title={
          <Space>
            <DollarOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
            <span style={{ fontWeight: 600 }}>Remboursement Mobile Money (FineoPay Payout)</span>
          </Space>
        }
        open={refundModal}
        onCancel={() => !refundSubmitting && setRefundModal(false)}
        footer={[
          <Button key="cancel" disabled={refundSubmitting} onClick={() => setRefundModal(false)}>
            Annuler
          </Button>,
          <Button
            key="submit"
            type="primary"
            danger
            loading={refundSubmitting}
            icon={<DollarOutlined />}
            onClick={handleExecuteRefund}
          >
            Effectuer le virement
          </Button>
        ]}
        width={560}
      >
        <div style={{ padding: '8px 0' }}>
          <Alert
            message="Transfert instantané de fonds"
            description="Le montant sélectionné sera directement débité de votre solde marchand FineoPay et crédité instantanément sur le compte Mobile Money du client."
            type="warning"
            showIcon
            style={{ marginBottom: 20 }}
          />

          <Form layout="vertical">
            <Form.Item label="Opérateur Mobile Money" required>
              <Radio.Group
                value={refundCanal}
                onChange={(e) => setRefundCanal(e.target.value)}
                style={{ width: '100%' }}
              >
                <Space size="middle" wrap>
                  <Radio.Button value="wave" style={{ borderRadius: 6, fontWeight: 500 }}>
                    <span style={{ color: '#1BA7EC', marginRight: 4 }}>●</span> Wave (Min. 200 F)
                  </Radio.Button>
                  <Radio.Button value="orange" style={{ borderRadius: 6, fontWeight: 500 }}>
                    <span style={{ color: '#FF7900', marginRight: 4 }}>●</span> Orange (Min. 100 F)
                  </Radio.Button>
                  <Radio.Button value="mtn" style={{ borderRadius: 6, fontWeight: 500 }}>
                    <span style={{ color: '#FFCC00', marginRight: 4 }}>●</span> MTN (Min. 100 F)
                  </Radio.Button>
                  <Radio.Button value="moov" style={{ borderRadius: 6, fontWeight: 500 }}>
                    <span style={{ color: '#005CA9', marginRight: 4 }}>●</span> Moov (Min. 200 F)
                  </Radio.Button>
                </Space>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              label="Numéro de téléphone du bénéficiaire"
              required
              extra="Format accepté : 0700000000 ou 2250700000000"
            >
              <Input
                prefix={<PhoneOutlined style={{ color: '#bfbfbf' }} />}
                placeholder="Ex: 0749557101"
                value={refundPhone}
                onChange={(e) => setRefundPhone(e.target.value)}
              />
            </Form.Item>

            <Form.Item label="Montant du remboursement (FCFA)" required>
              <InputNumber
                style={{ width: '100%' }}
                min={100}
                placeholder="Montant en FCFA"
                value={refundAmount}
                onChange={(val) => setRefundAmount(val ?? undefined)}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                parser={(value) => Number(value?.replace(/\s?|(,*)/g, '') || 0)}
              />
            </Form.Item>

            <Form.Item label="Motif / Note interne">
              <Input.TextArea
                rows={2}
                placeholder="Ex: Annulation client suite à indisponibilité..."
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
              />
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default InterventionsPage;

